// src/utils/markdownOrNode.js
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';

/**
 * Renders children as-is, unless `mark` is true.
 * When `mark` is true, any plain string/number in the tree
 * is passed through ReactMarkdown. Elements are preserved,
 * but their children get processed recursively.
 *
 * This version overrides ReactMarkdown’s <p> to be a Fragment,
 * preventing nested <p>–in–<p> warnings.
 */
export function MarkdownOrNode({
  children,
  mark = false,
  components = {},
  remarkPlugins = [],
  ...props
}) {
  // Custom <a> renderer with security attributes
  const customLink = ({ node, ...linkProps }) => (
    <a {...linkProps} target="_blank" rel="noopener noreferrer" />
  );

  // If not marking, render everything unchanged
  if (!mark) {
    return <>{children}</>;
  }

  // Recursive processor
  function processChild(child) {
    // 1) Plain text or number → Markdown with <p>→Fragment override
    if (typeof child === 'string' || typeof child === 'number') {
      const text = JSON.parse(JSON.stringify(String(child))).replace(/\n\n/g, '</br></br>');
      return (
        <ReactMarkdown
          {...props}
          remarkPlugins={[remarkBreaks, ...remarkPlugins]}
          rehypePlugins={[rehypeRaw]}
          components={{
            p: React.Fragment,
            a: customLink,
            // you can also override heading tags here if you want
            ...components,
          }}
        >
          {text}
        </ReactMarkdown>
      );
    }

    // 2) Array → process each entry
    if (Array.isArray(child)) {
      return child.map((c, i) => <React.Fragment key={i}>{processChild(c)}</React.Fragment>);
    }

    // 3) React element → clone and recurse into its children
    if (React.isValidElement(child)) {
      return React.cloneElement(
        child,
        // preserve existing props except key
        undefined,
        processChild(child.props.children),
      );
    }

    // 4) Anything else → pass through (boolean, null, etc.)
    return child;
  }

  // Kick off recursive processing
  return <>{processChild(children)}</>;
}
